import { useEffect, useRef } from 'react';

// Full-screen training video. The <video> element is owned by the controller (so play() runs inside the click); this
// only hosts it. Never auto-skips: a blocked/failed play shows a big host button; Esc / S skip.
export const TrainingVideoOverlay = ({ video, vs, hud, onSkip }) => {
  const box = useRef(null);

  useEffect(() => {
    const host = box.current;
    host.prepend(video.el);
    return () => video.el.remove();
  }, [video]);

  useEffect(() => {
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      if (k !== 'escape' && k !== 's') return;
      e.preventDefault();
      e.stopImmediatePropagation(); // S must not toggle the simulator here
      onSkip();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onSkip]);

  const loading = !vs.needsClick && (vs.playResult === 'pending' || (!vs.paused && vs.readyState < 3));
  const pct = vs.duration ? Math.round((vs.buffered / vs.duration) * 100) : Math.round((vs.progress || 0) * 100);
  return (
    <div ref={box} data-testid="training-video-overlay" className={`fixed inset-0 z-[70] bg-black ${vs.needsClick ? '' : 'cursor-none'}`}>
      {loading && (
        <div data-testid="training-video-loading" className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="label-eyebrow text-[var(--muted)]">LOADING TRAINING VIDEO… {pct}%</span>
        </div>
      )}
      {vs.needsClick && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-black/70">
          <button
            type="button"
            autoFocus
            data-testid="training-video-play-button"
            onClick={video.retry}
            className="border-2 border-[var(--spore)] bg-[#10b98122] px-14 py-8 font-display text-5xl font-black tracking-wide text-[var(--spore)] outline-none transition-[background-color,transform] duration-200 hover:-translate-y-1 hover:bg-[#10b98144] focus-visible:-translate-y-1"
          >
            ▶ PLAY TRAINING VIDEO
          </button>
          <p data-testid="training-video-error" className="max-w-[70vw] text-center text-xs tracking-[0.2em] text-[var(--amber)]">
            {vs.error || 'PLAYBACK BLOCKED BY BROWSER'} · ESC SKIPS
          </p>
        </div>
      )}
      {hud && (
        <div data-testid="training-video-debug" className="absolute bottom-2 left-2 bg-black/80 px-2 py-1 font-mono text-[11px] text-[var(--dim)]">
          src {vs.source || '—'} · ready {vs.readyState} · net {vs.networkState} · t {vs.currentTime}/{Math.round(vs.duration || 0)} · buf {vs.buffered}s · play {vs.playResult || '—'} · preload {Math.round((vs.progress || 0) * 100)}%{vs.iframe ? ' · iframe' : ''}{vs.error ? ` · ${vs.error}` : ''}
        </div>
      )}
    </div>
  );
};
